import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import Grid from "../../../components/Grid";
import { MoreHorizontal, UsersRound } from "lucide-react";
import { ShowBtn } from "@/components/Show";
import { CreateLoaderInput } from "../../../components/Loaders";
import { DeleteBtn } from "@/components/Delete";
import { TypeTableProps } from "@/App/Plans/Table";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { ComponentSwitchToggleStatus } from "@/App/Courses/ActivedOrDisabled";
import { UpdateBtn } from "../Update";
import { TableDataProps } from "@/pages/Units";
import { Mask } from "@/utils/mask";
import {
  setStatusUnities,
  getShowUnities,
  deleteUnitiesData,
} from "@/Api/unities";
import LogoFavicon from "../../../../public/images/logo-favicon.png";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { usePermissions } from "@/hooks/usePermissions";
import { If } from "@/components/If";

export const TableData = ({
  dataApi,
  pageActions,
  updateFront,
}: TypeTableProps<TableDataProps>) => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  return (
    <>
      <Table className="bg-zinc-50 rounded dark:bg-[#2c2c2c]">
        <TableHeader>
          <TableRow>
            <TableHead>Foto</TableHead>
            <TableHead>Nome</TableHead>

            <TableHead className="text-center">Ativo</TableHead>
            <TableHead className="text-center">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {dataApi?.data.map((item, index) => {
            const mainUnit = item?.id == 1;
            return (
              <TableRow key={item?.id}>
                <TableCell>
                  <img
                    src={item.image || LogoFavicon}
                    alt={item.name}
                    className="size-7 aspect-square object-cover rounded"
                  />
                </TableCell>
                <TableCell className="truncate max-w-80">
                  {item?.name}
                </TableCell>

                {!mainUnit ? (
                  <TableCell>
                    <ComponentSwitchToggleStatus
                      defaultChecked={item?.is_active}
                      params={{
                        func: setStatusUnities,
                        funcParams: item?.uuid,
                      }}
                      updateFront={() =>
                        updateFront((prev) => {
                          return {
                            ...prev,
                            data: prev?.data?.map((values) => {
                              if (values?.uuid === item?.uuid)
                                return {
                                  ...values,
                                  is_active: !values?.is_active,
                                };
                              return values;
                            }),
                          };
                        })
                      }
                    />
                  </TableCell>
                ) : (
                  <TableCell> </TableCell>
                )}
                <TableCell className="flex justify-center">
                  <div className="flex gap-3 w-full justify-center">
                    <If condition={hasPermission("show-unit-responsible")}>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="mx-1 size-7 p-0 text-slate-900 hover:bg-stone-950/10 dark:text-[#F4F4F5]"
                        onClick={() =>
                          navigate(`/unidades/${item?.uuid}/usuarios`)
                        }
                      >
                        <UsersRound className="size-5" />
                      </Button>
                    </If>
                    <If condition={hasPermission("edit-unit")}>
                      <UpdateBtn
                        uuid={item?.uuid}
                        updateFront={(response) => {
                          updateFront((prev) => {
                            prev?.data?.splice(index, 1, response);
                            return {
                              ...prev,
                              data: [...prev.data],
                            };
                          });
                        }}
                      />
                    </If>
                    <If condition={!mainUnit}>
                      <If condition={hasPermission("delete-unit")}>
                        <DeleteBtn
                          params={{
                            func: deleteUnitiesData,
                            funcParams: item?.uuid,
                          }}
                          updateFront={() =>
                            updateFront((prev) => ({
                              ...prev,
                              data: [
                                ...prev?.data?.filter(
                                  (data) => data?.uuid !== item?.uuid
                                ),
                              ],
                            }))
                          }
                          referenceLabel={item?.name}
                        />
                      </If>
                    </If>

                    <If condition={hasPermission("show-unit-details")}>
                      <ShowBtn
                        labels={{ title: "Visualizar Unidade" }}
                        params={{
                          func: getShowUnities,
                          funcParams: item?.uuid,
                        }}
                        render={(responseApi: TableDataProps) => (
                          <div className="w-full h-auto">
                            <div className="flex flex-col gap-3">
                              <Grid.Item name="Nome" flex={0}>
                                {responseApi?.name}
                              </Grid.Item>
                              <Grid.Item name="Logo" flex={0}>
                                <img
                                  src={responseApi?.image || LogoFavicon}
                                  alt={responseApi?.name}
                                  className="w-full aspect-square object-cover rounded"
                                />
                              </Grid.Item>
                              <Grid.Item name="Data de criação" flex={0}>
                                {responseApi?.created_at
                                  ? Mask.date(responseApi?.created_at)
                                  : "Não informado"}
                              </Grid.Item>
                              <Grid.Item name="Data de atualização" flex={0}>
                                {responseApi?.updated_at
                                  ? Mask.date(responseApi?.updated_at)
                                  : "Não informado"}
                              </Grid.Item>
                            </div>
                          </div>
                        )}
                        loader={<CreateLoaderInput lines={4} />}
                      />
                    </If>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
          {dataApi?.data.length < 1 && (
            <TableRow>
              <TableCell colSpan={5}>Nada foi encontrado.</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <div className="flex justify-between mb-3">
        {dataApi?.data.length > 0 && (
          <span className="font-medium">
            Exibindo {dataApi?.current_page} de um total de {dataApi?.last_page}
          </span>
        )}
        {dataApi?.last_page > 1 && (
          <Pagination className="">
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  className={cn("h-8", colors.bg_primary)}
                  onClick={pageActions.previous}
                />
              </PaginationItem>
              <PaginationItem>
                <PaginationLink
                  href="#"
                  className={cn(
                    `size-8 bg-transparent border-2 border-[#023859] text-slate-900 font-bold cursor-auto hover:bg-transparent`
                  )}
                >
                  <span className="pointer-events-none dark:text-[#F4F4F5]">
                    {dataApi?.current_page}
                  </span>
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationLink
                  href="#"
                  className="text-black h-8 hover:bg-stone-950/10"
                  onClick={pageActions.lastPage}
                >
                  <MoreHorizontal className="h-4 w-4 dark:text-[#F4F4F5]" />
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationNext
                  href="#"
                  className={cn("h-8", colors.bg_primary)}
                  onClick={pageActions.next}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        )}
      </div>
    </>
  );
};
